/**
 * MADE-065: InventoryPriorityPanel
 * Collapsible Section B: unified produce list + stock alerts.
 * Buy items → "BLOCKED on procurement" badge (no button).
 * Produce items → "Send to Production" button → spawns ManufacturingOrder.
 * Deduplicates items that appear in both stock alerts and produce list.
 */

import React, { useState } from 'react';
import {
  Box, Typography, Stack, Paper, Chip, Button, Alert,
  Collapse, IconButton, CircularProgress,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Block as BlockIcon,
  Inventory as InventoryIcon,
} from '@mui/icons-material';
import type { Material, StockAlert } from '../../types/madebb.types';

interface InventoryPriorityPanelProps {
  produceList: Material[];
  stockAlerts: StockAlert[];
  onSendToProduction: (materialId: string, quantity: number, materialName: string, bom?: Material['billOfMaterials']) => Promise<void>;
  sendingIds: Set<string>;
  onResolveAlert: (alertId: number) => void;
}

export const InventoryPriorityPanel: React.FC<InventoryPriorityPanelProps> = ({
  produceList, stockAlerts, onSendToProduction, sendingIds, onResolveAlert,
}) => {
  const [expanded, setExpanded] = useState(true);

  // Filter produce list to only show items that need action
  const actionableProduceList = produceList.filter(
    item => (item.currentStock + (item.inProductionQty || 0)) < item.minStock
  );

  // Deduplicate: build a set of material IDs from produce list
  const produceIds = new Set(actionableProduceList.map(m => m.id));

  // Stock alerts for materials not already in the produce list,
  // and only where combined coverage (current + in-production) is still below threshold.
  const alertsOnly = stockAlerts.filter(
    a => !produceIds.has(a.material_id) &&
      calcCombinedCoverage(a.current_stock, a.in_production_qty) < a.threshold
  );

  const hasItems = actionableProduceList.length > 0 || alertsOnly.length > 0;

  return (
    <Paper elevation={1} sx={{ mb: 2 }}>
      {/* Panel header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2, py: 1.5, bgcolor: 'grey.100', borderRadius: expanded ? '8px 8px 0 0' : '8px' }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <InventoryIcon fontSize="small" color="action" />
          <Typography variant="subtitle1" fontWeight={700}>
            Inventory & Priority
          </Typography>
          {(actionableProduceList.length > 0 || alertsOnly.length > 0) && (
            <Chip
              label={`${actionableProduceList.length + alertsOnly.length} items`}
              size="small"
              color={alertsOnly.length > 0 ? 'warning' : 'default'}
            />
          )}
        </Stack>
        <IconButton size="small" onClick={() => setExpanded(v => !v)}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Stack>

      <Collapse in={expanded}>
        <Box sx={{ p: 2 }}>
          {!hasItems && (
            <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
              No low-stock items or alerts.
            </Typography>
          )}

          {/* Produce items */}
          {actionableProduceList.map(item => (
            <ProduceRow
              key={item.id}
              item={item}
              onSendToProduction={onSendToProduction}
              isSending={sendingIds.has(item.id)}
            />
          ))}

          {/* Stock alerts for items not in produce list */}
          {alertsOnly.map(alert => (
            <AlertRow key={alert.id} alert={alert} onResolve={() => onResolveAlert(alert.id)} />
          ))}
        </Box>
      </Collapse>
    </Paper>
  );
};

// ── ProduceRow ───────────────────────────────────────────────────────────────

interface ProduceRowProps {
  item: Material;
  onSendToProduction: (materialId: string, quantity: number, materialName: string, bom?: Material['billOfMaterials']) => Promise<void>;
  isSending: boolean;
}

const ProduceRow: React.FC<ProduceRowProps> = ({ item, onSendToProduction, isSending }) => {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ py: 1, borderBottom: '1px solid', borderColor: 'divider' }}
    >
      <Box flex={1}>
        <Typography variant="body2" fontWeight={600}>{item.name}</Typography>
        <Typography variant="caption" color="text.secondary">
          Stock: {item.currentStock} / Min: {item.minStock} {item.unit}
          {item.inProductionQty > 0 && ` / In Prod: ${item.inProductionQty}`}
          {' — '}
          <strong>{item.currentStock + (item.inProductionQty || 0)} units covered</strong>
        </Typography>
      </Box>

      <Stack direction="row" alignItems="center" spacing={1}>
        {item.buyOrProduce === 'buy' ? (
          <Chip
            icon={<BlockIcon />}
            label="BLOCKED on procurement"
            size="small"
            color="error"
            variant="outlined"
            sx={{ fontSize: 10 }}
          />
        ) : (
          <>
            <Chip label="Produce" size="small" color="secondary" sx={{ fontSize: 10 }} />
            <Button
              size="small"
              variant="contained"
              disabled={isSending}
              startIcon={isSending ? <CircularProgress size={12} /> : undefined}
              onClick={() => onSendToProduction(item.id, item.reorderQuantity, item.name, item.billOfMaterials)}
              sx={{ fontSize: 11, whiteSpace: 'nowrap' }}
            >
              Send to Production
            </Button>
          </>
        )}
      </Stack>
    </Stack>
  );
};

// ── MADE-089: Combined coverage helpers ──────────────────────────────────────

/** Returns current_stock + in_production_qty (safe default 0). */
export const calcCombinedCoverage = (currentStock: number, inProductionQty?: number): number =>
  currentStock + (inProductionQty || 0);

/** Returns true when combined coverage is still below min_stock (item needs action). */
export const isCoverageInsufficient = (currentStock: number, inProductionQty: number | undefined, minStock: number): boolean =>
  calcCombinedCoverage(currentStock, inProductionQty) < minStock;

// ── AlertRow ─────────────────────────────────────────────────────────────────

interface AlertRowProps {
  alert: StockAlert;
  onResolve: () => void;
}

const AlertRow: React.FC<AlertRowProps> = ({ alert, onResolve }) => {
  const combined = calcCombinedCoverage(alert.current_stock, alert.in_production_qty);
  return (
    <Alert
      severity="warning"
      action={
        <Button color="inherit" size="small" onClick={onResolve}>
          Dismiss
        </Button>
      }
      sx={{ mb: 0.5 }}
    >
      <Typography variant="body2">
        <strong>{alert.material_name}</strong> — {alert.current_stock} {alert.unit || 'units'} in stock
        {alert.in_production_qty ? ` + ${alert.in_production_qty} in production` : ''}
        {' '}(<strong>{combined} units covered</strong>; threshold: {alert.threshold})
      </Typography>
    </Alert>
  );
};
